<template>
  <div class="dashboard-stat-card" @click="handleClick">
    <div class="dashboard-stat-label">{{ label }}</div>
    <div class="dashboard-stat-value">{{ formattedValue }}</div>
    <div v-if="change !== null" :class="changeClasses">
      <svg
        v-if="changeType === 'positive'"
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
      >
        <polyline points="18 15 12 9 6 15" />
      </svg>
      <svg
        v-else-if="changeType === 'negative'"
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
      >
        <polyline points="6 9 12 15 18 9" />
      </svg>
      <svg
        v-else
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
      >
        <line x1="5" y1="12" x2="19" y2="12" />
      </svg>
      <span>{{ changeText }}</span>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  /**
   * Stat label/title
   */
  label: {
    type: String,
    required: true,
  },
  /**
   * Stat value
   */
  value: {
    type: [String, Number],
    required: true,
  },
  /**
   * Format as currency
   */
  currency: {
    type: Boolean,
    default: false,
  },
  /**
   * Currency symbol
   */
  currencySymbol: {
    type: String,
    default: '₹',
  },
  /**
   * Change percentage or value
   */
  change: {
    type: Number,
    default: null,
  },
  /**
   * Change type
   * @values positive, negative, neutral
   */
  changeType: {
    type: String,
    default: 'neutral',
    validator: (value) => ['positive', 'negative', 'neutral'].includes(value),
  },
  /**
   * Change text
   */
  changeText: {
    type: String,
    default: '',
  },
  /**
   * Animation delay (for staggered effect)
   */
  animationDelay: {
    type: Number,
    default: 0,
  },
  /**
   * Make card clickable
   */
  clickable: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['click'])

const formattedValue = computed(() => {
  if (props.currency) {
    // Format number with commas
    const numValue = typeof props.value === 'number' ? props.value : parseFloat(props.value)
    return `${props.currencySymbol}${numValue.toLocaleString('en-IN')}`
  }
  return props.value
})

const changeClasses = computed(() => [
  'dashboard-stat-change',
  `dashboard-stat-change--${props.changeType}`,
])

const handleClick = () => {
  if (props.clickable) {
    emit('click')
  }
}
</script>

<style scoped>
/* ========================================
   DASHBOARD STAT CARD
   ======================================== */
.dashboard-stat-card {
  background: var(--color-white);
  padding: var(--space-6);
  border-radius: var(--radius-lg);
  border: 1px solid var(--color-gray-200);
  transition: all var(--transition-base);
  cursor: pointer;
  animation: fadeInUp 0.4s ease-out backwards;
  animation-delay: var(--animation-delay, 0s);
}

.dashboard-stat-card:hover {
  transform: translateY(-3px);
  box-shadow: var(--shadow-lg);
  border-color: var(--color-blue);
}

/* ========================================
   STAT CONTENT
   ======================================== */
.dashboard-stat-label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-gray-500);
  margin-bottom: var(--space-3);
  text-transform: uppercase;
  letter-spacing: var(--letter-spacing-wide);
}

.dashboard-stat-value {
  font-size: var(--font-size-3xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-charcoal);
  margin-bottom: var(--space-2);
  font-variant-numeric: tabular-nums;
  letter-spacing: var(--letter-spacing-tight);
}

.dashboard-stat-change {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
}

.dashboard-stat-change--positive {
  color: var(--color-success);
}

.dashboard-stat-change--negative {
  color: var(--color-error);
}

.dashboard-stat-change--neutral {
  color: var(--color-gray-500);
}

/* ========================================
   ANIMATION
   ======================================== */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
